/**
 * DeviceDisplayNames.ts
 *
 * Converts raw browser MediaDevice labels into human-readable names.
 *
 * To add a new device rename rule:
 *   1. Add an entry to DEVICE_DISPLAY_RULES below.
 *   2. Set `match` to a regex that uniquely identifies the device model string.
 *   3. Set `transform` to a function that receives the raw label and returns
 *      the display name you want users to see.
 *
 * Rules are tested in order; the first match wins.
 * Devices that match no rule keep their original browser label.
 */

interface DeviceDisplayRule {
    match: RegExp;
    transform: (rawLabel: string) => string;
    /** Optional further shortening applied only in the collapsed selector field. */
    compactTransform?: (friendlyName: string) => string;
}

const DEVICE_DISPLAY_RULES: DeviceDisplayRule[] = [
    {
        // Matches devices containing "USB AUDIO CODEC" whose label does NOT
        // start with "Communications" (those are kept with their original name).
        // e.g. "Line (2- USB AUDIO  CODEC) (08bb:29c6)"
        //   → "Enersound: 2-USB AUDIO CODEC :29c6"  (full / expanded)
        //   → "Enersound:USB A CODEC:29c6"           (compact / collapsed)
        match: /^(?!Communications).*USB\s+AUDIO\s+CODEC/i,
        transform: (raw) => {
            // Collapse all internal whitespace first so the rest of the regexes
            // don't need to handle double-space variants.
            const normalized = raw.replace(/\s+/g, ' ');

            // Extract the "(N- USB AUDIO CODEC)" group → "2-USB AUDIO CODEC"
            const modelMatch = normalized.match(/\((\d+\s*-\s*USB AUDIO CODEC)\)/i);
            // Extract the last 4 hex digits from "(xxxx:yyyy)" → "yyyy"
            const idMatch    = normalized.match(/\([0-9a-f]+:([0-9a-f]+)\)/i);

            // Remove spaces to compact "2- USB AUDIO CODEC" → "2-USB AUDIO CODEC"
            const model = modelMatch
                ? modelMatch[1].replace(/\s*-\s*/, '-').replace(/\s+/g, ' ').trim()
                : 'USB AUDIO CODEC';
            const id = idMatch ? idMatch[1] : '';

            return `Enersound: ${model}${id ? ' :' + id : ''}`;
        },
        // Collapsed field: remove spaces around colons and shorten "AUDIO" → "A"
        compactTransform: (friendly) =>
            friendly
                .replace(/\bAUDIO\b/g, 'A')
                .replace(/\s*:\s*/g, ':'),
    },

    // ── Add more rules here ────────────────────────────────────────────────
    // Example:
    // {
    //     match: /Logitech Webcam C925e/i,
    //     transform: () => 'Logitech C925e',
    // },
    // ──────────────────────────────────────────────────────────────────────
];

/**
 * Returns a friendly display name for an audio device label.
 * Falls back to the original browser label if no rule matches.
 */
export function getFriendlyDeviceName(rawLabel: string): string {
    for (const rule of DEVICE_DISPLAY_RULES) {
        if (rule.match.test(rawLabel)) {
            return rule.transform(rawLabel);
        }
    }
    return rawLabel;
}

/**
 * Returns a compact display name suitable for the collapsed device-selector field.
 *
 * Devices that match a rename rule use that rule's `compactTransform` when
 * available, otherwise the standard `transform` result is returned as-is.
 *
 * For all other devices the following transformations are applied in order:
 *   1. The trailing hex-id group "(xxxx:yyyy)" is replaced with " :yyyy"
 *      (only the last 4-char product-id is kept).
 *   2. Any remaining parentheses are stripped (content is preserved).
 *   3. "Microphone" is abbreviated to "Mic:" and "Speaker" to "Spk:".
 *   4. Windows-style numbering prefix "N- " is compacted to "N-".
 *   5. Runs of spaces are collapsed and the result is trimmed.
 *
 * Example:
 *   "Microphone (2- Sennheiser SC60 for Lync) (1395:0033)"
 *   → "Mic: 2-Sennheiser SC60 for Lync :0033"
 */
export function getCompactDeviceName(rawLabel: string): string {
    // Already covered by a rename rule → apply its compact variant if defined
    for (const rule of DEVICE_DISPLAY_RULES) {
        if (rule.match.test(rawLabel)) {
            const friendly = rule.transform(rawLabel);
            return rule.compactTransform ? rule.compactTransform(friendly) : friendly;
        }
    }

    let name = rawLabel;

    // Replace hex-id group "(xxxx:yyyy)" → " :yyyy", consuming surrounding spaces
    name = name.replace(/\s*\([0-9a-f]+:([0-9a-f]+)\)\s*/gi, ' :$1 ');

    // Strip remaining parentheses (keep content)
    name = name.replace(/[()]/g, '');

    // Drop "Microphone" or "Speaker(s)" only when it is the first word
    name = name.replace(/^(Microphone|Speakers?)\s*/i, '');

    // Compact Windows numbering "N- text" → "N-text"
    name = name.replace(/(\d+)\s*-\s*/g, '$1-');

    // Normalise whitespace
    return name.replace(/\s+/g, ' ').trim();
}
